import styles from '../Id.module.scss'
import { formatCreateDate } from 'lib/functions'
import { Post } from 'interfaces/posts'
import { useEffect, useState, useRef } from 'react'
import Breadcrumb from 'components/commons/Breadcrumb/Breadcrumb'
import CompanyRenameNotice from 'components/topics/CompanyRenameNotice'
import ActiveEvent from 'components/topics/ActiveEvent/ActiveEvent'
import { useRouter } from 'next/router'
import Button from 'components/commons/Button/Button'
import FooterCTA from 'components/common/FooterCTA'
import loadHubSpotFormsScript from 'lib/loadHubSpotFormsScript'
import animationStyles from 'components/common/Animation/Animation.module.scss'
import useElementVisible from 'components/common/Animation/useElementVisible'

const statusToClassMap = {
  赤: 'statusIcon_red',
  赤枠: 'statusIcon_red_framed',
  青: 'statusIcon_blue',
  青枠: 'statusIcon_blue_framed',
  ピンク: 'statusIcon_pink',
  ピンク枠: 'statusIcon_pink_framed',
  オレンジ: 'statusIcon_orange',
  オレンジ枠: 'statusIcon_orange_framed',
  緑: 'statusIcon_green',
  緑枠: 'statusIcon_green_framed',
  紫: 'statusIcon_purple',
  紫枠: 'statusIcon_purple_framed',
  グレー: 'statusIcon_gray',
  グレー枠: 'statusIcon_gray_framed',
  薄グレー: 'statusIcon_light_gray',
  白: 'statusIcon_white',
}

const getStatusClass = (status) => {
  const baseClass = `${styles.statusIcon}`
  const specificClassName = statusToClassMap[status] || ''
  const specificClass = specificClassName ? styles[specificClassName] || '' : ''
  const fullClass = specificClass ? `${baseClass} ${specificClass}` : baseClass
  return fullClass.trim()
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土']
  const dayOfWeek = daysOfWeek[date.getDay()]
  return {
    fullDate: `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${dayOfWeek}）`,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    dayOfWeek: dayOfWeek,
  }
}

const getEventDateDisplay = (post) => {
  const startInfo = post.start_date ? formatDate(post.start_date) : null
  const endInfo = post.end_date ? formatDate(post.end_date) : null

  if (startInfo && endInfo) {
    if (startInfo.fullDate === endInfo.fullDate) {
      return startInfo.fullDate
    } else if (startInfo.year === endInfo.year) {
      if (startInfo.month === endInfo.month) {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.day}日（${endInfo.dayOfWeek}）`
      } else {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.month}月${endInfo.day}日（${endInfo.dayOfWeek}）`
      }
    } else {
      return `${startInfo.fullDate}〜${endInfo.fullDate}`
    }
  } else {
    return startInfo?.fullDate || endInfo?.fullDate || ''
  }
}

const Id: React.FC<Post> = ({ post, prevPost, nextPost, categoryName, articleHtml, activeEvents }) => {
  const router = useRouter()
  const { page } = router.query
  const sectionRef = useRef<HTMLElement>(null)
  const isSectionVisible = useElementVisible(sectionRef, 0.1)

  const [breadcrumbPaths, setBreadcrumbPaths] = useState([
    { name: 'TOP', url: '/' },
    { name: 'Topics', url: page ? `/pressrelease/page/${page}` : '/pressrelease/page/1' },
  ])

  useEffect(() => {
    if (post && post.title && post.title.rendered) {
      const newBreadcrumb = { name: post.title.rendered, url: `/${categoryName}/${post.id}` }

      const previousPath = page ? `/news/page/${page}` : '/news/page/1'
      const updatedPaths = [{ name: 'TOP', url: '/' }, { name: 'Topics', url: previousPath }, newBreadcrumb]
      setBreadcrumbPaths(updatedPaths)
    }
  }, [post, page])

  useEffect(() => {
    if (document.querySelector('.hbspt-form')) {
      const hbsptformData = document
        .querySelector('.hbspt-form')
        .previousElementSibling.innerHTML.replace(/\n|hbspt.forms.create\({|}\);|\s/g, '')
        .split(',')
      const timer = window.setTimeout(() => {
        loadHubSpotFormsScript()
          .then(() => {
            if (!window.hbspt?.forms?.create) {
              return
            }
            window.hbspt.forms.create({
              portalId: hbsptformData[0].replace(/.*:/, '').toString(),
              formId: hbsptformData[1].replace(/.*:|"/g, ''),
              target: hbsptformData[2].replace(/.*:|"/g, ''),
              region: hbsptformData[3].replace(/.*:|"/g, ''),
            })
          })
          .catch(() => {
            // スクリプト読み込み失敗時はフォームを生成しない
          })
      }, 100)

      return () => {
        window.clearTimeout(timer)
      }
    }
  }, [post])

  const twitterShareUrl = `https://twitter.com/share?url=${encodeURIComponent(
    `https://aitria.co.jp/news/${post.id}`,
  )}&text=${encodeURIComponent(`${post.title.rendered} | Aitria（アイトリア）`)}`
  const facebookShareUrl = `http://www.facebook.com/share.php?u=${encodeURIComponent(
    `https://aitria.co.jp/news/${post.id}`,
  )}`
  const hatenaBookmarkUrl = `http://b.hatena.ne.jp/add?mode=confirm&url=${encodeURIComponent(
    `https://aitria.co.jp/news/${post.id}`,
  )}&title=${encodeURIComponent(`${post.title.rendered} | Aitria（アイトリア）`)}`
  const pocketShareUrl = `http://getpocket.com/edit?url=${encodeURIComponent(
    `https://aitria.co.jp/news/${post.id}`,
  )}&title=${encodeURIComponent(`${post.title.rendered} | Aitria（アイトリア）`)}`

  return (
    <>
      <article>
        <section
          ref={sectionRef}
          className={`${styles.section} ${animationStyles.fade} ${isSectionVisible ? animationStyles.fadeOn : ''}`}
          key={post.id}
        >
          <div className={styles.breadcrumbs}>
            <Breadcrumb paths={breadcrumbPaths} />
          </div>
          <div className={styles.topicsFlex}>
            <div className={styles.wrapper}>
              <CompanyRenameNotice date={post.date} />
              <p className={styles.release}>公開日：{formatCreateDate(post.date)}</p>
              <h1 className={styles.topicsTitle}>{post.title.rendered}</h1>
              <div className={styles.container} dangerouslySetInnerHTML={{ __html: articleHtml }}></div>
              <div className={styles.share}>
                <p>この記事をシェアする</p>
                <div className={styles.share_items}>
                  <a
                    href={twitterShareUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_x}`}
                  ></a>
                  <a
                    href={facebookShareUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_fb}`}
                  ></a>
                  <a
                    href={hatenaBookmarkUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_hatena}`}
                  ></a>
                  <a
                    href={pocketShareUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_pocket}`}
                  ></a>
                </div>
              </div>
              <div className={styles.flex}>
                {prevPost ? (
                  <a href={`/${categoryName}/${prevPost.id}`} className={`${styles.link} ${styles.prevlink}`}>
                    <img src="/direction-reverse.svg" className={styles.prevDirection_img} alt={'Prev'} />
                    <div className={styles.is_pc}>
                      {prevPost.start_date && (
                        <div className={styles.link_date}>
                          <span>{getEventDateDisplay(prevPost)}</span>
                          <span>{prevPost.duration && ` ${prevPost.duration}`}</span>
                          <span>開催</span>
                        </div>
                      )}
                      <p className={styles.link_title}>{prevPost.title.rendered}</p>
                    </div>
                    <p className={styles.is_sp}>前の記事を見る</p>
                  </a>
                ) : (
                  <div></div>
                )}
                {nextPost ? (
                  <a href={`/${categoryName}/${nextPost.id}`} className={`${styles.link} ${styles.nextlink}`}>
                    <div className={styles.is_pc}>
                      {nextPost.start_date && (
                        <div className={styles.link_date}>
                          <span>{getEventDateDisplay(nextPost)}</span>
                          <span>{nextPost.duration && ` ${nextPost.duration}`}</span>
                          <span>開催</span>
                        </div>
                      )}
                      <p className={styles.link_title}>{nextPost.title.rendered}</p>
                    </div>
                    <p className={styles.is_sp}>次の記事を見る</p>
                    <img src="/direction_black.svg" className={styles.nextDirection_img} alt={'Next'} />
                  </a>
                ) : (
                  <div></div>
                )}
              </div>
              <Button text="一覧に戻る" href={`/pressrelease/page/${page || 1}`} />
            </div>
            <ActiveEvent activeEvents={activeEvents} getEventDateDisplay={getEventDateDisplay} />
          </div>
          <div className={styles.mailmagazine_link}>
            <p>
              UX/UIデザイン・システム内製化・DX推進についてのお役立ちメソッドや、限定イベントや最新事例などをお届けします。
            </p>
            <div className={styles.mailmagazine_link_button}>
              <Button text="メールマガジン" href="/mailmagazine" red="red" />
            </div>
          </div>
        </section>
        <FooterCTA />
      </article>
    </>
  )
}

export default Id
